import React from 'react';
import { useSelector } from 'react-redux';
import { Stack } from '@qred/components-library';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import { useGetBankAccountsQuery } from '~/store/apis/endpoints/onboardingApi/onboarding.api';
import { RootState } from '~/store';
import { useLoanOfferSelectors } from '~/store/selectors/loanOffer.selector';
import useManualAccountForm from '../../../hooks/useManualAccountForm';
import { useBankInformationFooter } from '../BankInformationFooterContext';
import BackToSearchLink from './BackToSearchLink';
import BackToVerifiedAccountsLink from './BackToVerifiedAccountsLink';

const BankInformationHeader = () => {
  const {
    overview: { bankAccounts, clientId },
  } = useSelector((state: RootState) => state.loanOffer);
  const { hasSepaDirectDebitFeature } = useLoanOfferSelectors();
  const { showAddNewAccount } = useBankInformationFooter();
  const {
    isManualAccountFormShown,
    canGoBackToSearch,
  } = useManualAccountForm();
  const { data: openBankingAccounts } = useGetBankAccountsQuery(
    { clientId },
    {
      skip: !clientId,
    }
  );

  const hasBankAccountsViaKlarnaKosma = Boolean(bankAccounts?.length);
  const hasBankAccountsViaOpenBanking = Boolean(openBankingAccounts?.length);
  const isAddingDifferentAccount =
    hasBankAccountsViaOpenBanking &&
    showAddNewAccount &&
    !isManualAccountFormShown;

  const getDescription = () => {
    if (isManualAccountFormShown) {
      return hasSepaDirectDebitFeature
        ? 'OnboardingOffer.ManualAccountDescriptionWithRepayment'
        : 'OnboardingOffer.ManualAccountDescription';
    }

    return hasBankAccountsViaOpenBanking || hasBankAccountsViaKlarnaKosma
      ? 'OnboardingOffer.ConfirmBankAccountDescriptionIfExist'
      : 'OnboardingOffer.ConfirmBankAccountDescriptionIfNotExist';
  };

  const stepHeader = (
    <StepHeader
      title="OnboardingOffer.ConfirmBankAccountTitle"
      description={getDescription()}
    />
  );

  if (isManualAccountFormShown && !canGoBackToSearch) {
    return stepHeader;
  }

  if (isManualAccountFormShown || isAddingDifferentAccount) {
    return (
      <Stack spacing={'xl'}>
        {isManualAccountFormShown ? (
          <BackToSearchLink />
        ) : (
          <BackToVerifiedAccountsLink />
        )}
        {stepHeader}
      </Stack>
    );
  }

  return stepHeader;
};

export default BankInformationHeader;
